<script setup lang="ts">
import {
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  addDays,
  addMonths,
  addYears,
  format,
  min,
  max,
} from 'date-fns'
import { de } from 'date-fns/locale/de'
import OverlayPanel from 'primevue/overlaypanel'

const props = withDefaults(
  defineProps<{
    minDate: Date
    maxDate: Date
    primaryAspect?: 'date' | 'month' | 'year'
    sliderSteps?: number
    closeOnEscape?: boolean
  }>(),
  {
    primaryAspect: 'year',
    sliderSteps: 1,
    default: true,
  },
)

const model = defineModel<[from: Date | null, to: Date | null]>()

if (model.value === undefined) {
  model.value = [props.minDate, props.maxDate]
}
if (model.value[0] == null) {
  model.value[0] = props.minDate
}
if (model.value[1] == null) {
  model.value[1] = props.maxDate
}

const minDateInputRef = ref<HTMLInputElement>()
const maxDateInputRef = ref<HTMLInputElement>()

function toPrimaryAspectNumber(value: Date) {
  switch (props.primaryAspect) {
    case 'date':
      return differenceInDays(value, props.minDate)
    case 'month':
      return differenceInMonths(value, props.minDate)
    case 'year':
      return differenceInYears(value, props.minDate)
    default:
      throw new Error('Invalid primaryAspect')
  }
}

function modifyPrimaryAspectNumber(value: Date, by: number) {
  switch (props.primaryAspect) {
    case 'date':
      return addDays(value, by)
    case 'month':
      return addMonths(value, by)
    case 'year':
      return addYears(value, by)
    default:
      throw new Error('Invalid primaryAspect')
  }
}

function formatToPrimaryAspect(value: Date) {
  switch (props.primaryAspect) {
    case 'date':
      return format(value, 'dd.MM.yyyy')
    case 'month':
      return format(value, 'MMMM yyyy', { locale: de })
    case 'year':
      return String(value.getFullYear())
    default:
      throw new Error('Invalid primaryAspect')
  }
}

function useWritableFormattedDate(
  dateRef: Ref<Date>,
  validate: (value: Date) => boolean = () => true,
) {
  return computed<string>({
    get: () => formatToPrimaryAspect(dateRef.value),
    set: value => {
      const trimmedValue = value.trim()

      let newDateObject: Date
      if (/^[0-9]{1,4}$/.test(trimmedValue)) {
        let year = Number(trimmedValue)
        if (year < 100) {
          year += year < 50 ? 2000 : 1900
        }

        newDateObject = new Date(year, 0, 1, 0, 0, 0, 0)

        if (!validate(newDateObject)) {
          newDateObject = new Date(dateRef.value)
        }
      } else if (/^[0-9]{1,2}\/[0-9]{1,4}$/.test(trimmedValue)) {
        let [month, year] = trimmedValue.split('/').map(part => Number(part)) as [number, number]
        if (year < 100) {
          year += year < 50 ? 2000 : 1900
        }
        newDateObject = new Date(year, month - 1, 1, 0, 0, 0, 0)

        if (month < 1 || month > 12 || !validate(newDateObject)) {
          newDateObject = new Date(dateRef.value)
        }
      } else if (/^[0-9]{1,2}\.[0-9]{1,2}\.[0-9]{1,4}$/.test(trimmedValue)) {
        const [day, month, year] = trimmedValue.split('.').map(part => Number(part))
        newDateObject = new Date(year, month - 1, day, 0, 0, 0, 0)

        if (isNaN(newDateObject.getTime()) || !validate(newDateObject)) {
          newDateObject = new Date(dateRef.value)
        }
      } else {
        newDateObject = new Date(dateRef.value)
      }

      dateRef.value = newDateObject
    },
  })
}

function useAutoSizeInput(inputRef: Ref<HTMLInputElement | undefined>) {
  const resize = () => {
    const input = inputRef.value as HTMLInputElement

    const preValue = input.value
    const shouldUsePlaceholder = input.value.trim().length === 0 && input.placeholder.length > 0
    if (shouldUsePlaceholder) {
      input.value = input.placeholder
    }

    input.style.width = '0px'
    const additionalWidth = input.clientWidth
    input.style.width = `${input.scrollWidth + additionalWidth}px`

    if (shouldUsePlaceholder) {
      input.value = preValue
    }
  }

  onMounted(resize)
  useEventListener(inputRef, 'input', event => {
    resize()
  })

  return resize
}

const sliderMax = computed(() => toPrimaryAspectNumber(props.maxDate))
const sliderModel = computed({
  get() {
    return [
      min([selectedMinDate.value, selectedMaxDate.value]),
      max([selectedMinDate.value, selectedMaxDate.value]),
    ].map(date => toPrimaryAspectNumber(date))
  },
  set: value => {
    model.value = [
      modifyPrimaryAspectNumber(props.minDate, value[0]),
      modifyPrimaryAspectNumber(props.minDate, value[1]),
    ]
  },
})

const manuallyResizeMinDateInput = useAutoSizeInput(minDateInputRef)
const manuallyResizeMaxDateInput = useAutoSizeInput(maxDateInputRef)

const selectedMinDate = useListItem(model as Ref<Date[]>, 0, props.minDate)
const selectedMaxDate = useListItem(model as Ref<Date[]>, 1, props.maxDate)

const formattedSelectedMinDate = useWritableFormattedDate(
  selectedMinDate,
  value => value >= props.minDate && value <= props.maxDate && value <= selectedMaxDate.value,
)
watch(selectedMinDate, () => requestAnimationFrame(manuallyResizeMinDateInput))
const formattedSelectedMaxDate = useWritableFormattedDate(
  selectedMaxDate,
  value => value >= props.minDate && value <= props.maxDate && value >= selectedMinDate.value,
)
watch(selectedMaxDate, () => requestAnimationFrame(manuallyResizeMaxDateInput))

const minDateCalendarMax = computed(() => min([props.maxDate, selectedMaxDate.value]))
const maxDateCalendarMin = computed(() => max([props.minDate, selectedMinDate.value]))

const minDateCalendarPanel = ref<InstanceType<typeof OverlayPanel>>()
const maxDateCalendarPanel = ref<InstanceType<typeof OverlayPanel>>()
const toggleMinDateCalendar = (event: MouseEvent) => minDateCalendarPanel.value!.toggle(event)
const toggleMaxDateCalendar = (event: MouseEvent) => maxDateCalendarPanel.value!.toggle(event)
</script>

<template>
  <div>
    <div class="p-range-slider-layout">
      <div>
        <Button
          text
          class="p-button-icon-only"
          aria-label="Frühester Zeitpunkt"
          @click="toggleMinDateCalendar"
        >
          <!-- prettier-ignore -->
          <svg focusable="false" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="p-icon" aria-hidden="true" data-pc-section="dropdownicon"><path d="M10.7838 1.51351H9.83783V0.567568C9.83783 0.417039 9.77804 0.272676 9.6716 0.166237C9.56516 0.0597971 9.42079 0 9.27027 0C9.11974 0 8.97538 0.0597971 8.86894 0.166237C8.7625 0.272676 8.7027 0.417039 8.7027 0.567568V1.51351H5.29729V0.567568C5.29729 0.417039 5.2375 0.272676 5.13106 0.166237C5.02462 0.0597971 4.88025 0 4.72973 0C4.5792 0 4.43484 0.0597971 4.3284 0.166237C4.22196 0.272676 4.16216 0.417039 4.16216 0.567568V1.51351H3.21621C2.66428 1.51351 2.13494 1.73277 1.74467 2.12305C1.35439 2.51333 1.13513 3.04266 1.13513 3.59459V11.9189C1.13513 12.4709 1.35439 13.0002 1.74467 13.3905C2.13494 13.7807 2.66428 14 3.21621 14H10.7838C11.3357 14 11.865 13.7807 12.2553 13.3905C12.6456 13.0002 12.8649 12.4709 12.8649 11.9189V3.59459C12.8649 3.04266 12.6456 2.51333 12.2553 2.12305C11.865 1.73277 11.3357 1.51351 10.7838 1.51351ZM3.21621 2.64865H4.16216V3.59459C4.16216 3.74512 4.22196 3.88949 4.3284 3.99593C4.43484 4.10237 4.5792 4.16216 4.72973 4.16216C4.88025 4.16216 5.02462 4.10237 5.13106 3.99593C5.2375 3.88949 5.29729 3.74512 5.29729 3.59459V2.64865H8.7027V3.59459C8.7027 3.74512 8.7625 3.88949 8.86894 3.99593C8.97538 4.10237 9.11974 4.16216 9.27027 4.16216C9.42079 4.16216 9.56516 4.10237 9.6716 3.99593C9.77804 3.88949 9.83783 3.74512 9.83783 3.59459V2.64865H10.7838C11.0347 2.64865 11.2753 2.74831 11.4527 2.92571C11.6301 3.10311 11.7297 3.34371 11.7297 3.59459V5.67568H2.27027V3.59459C2.27027 3.34371 2.36993 3.10311 2.54733 2.92571C2.72473 2.74831 2.96533 2.64865 3.21621 2.64865ZM10.7838 12.8649H3.21621C2.96533 12.8649 2.72473 12.7652 2.54733 12.5878C2.36993 12.4104 2.27027 12.1698 2.27027 11.9189V6.81081H11.7297V11.9189C11.7297 12.1698 11.6301 12.4104 11.4527 12.5878C11.2753 12.7652 11.0347 12.8649 10.7838 12.8649Z" fill="currentColor"></path></svg>
        </Button>
      </div>
      <div class="p-range-slider-slider">
        <Slider v-model="sliderModel" range :max="sliderMax" :min="0" :step="sliderSteps" />
      </div>
      <div>
        <Button
          text
          class="p-button-icon-only"
          aria-label="Spätester Zeitpunkt"
          @click="toggleMaxDateCalendar"
        >
          <!-- prettier-ignore -->
          <svg focusable="false" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="p-icon" aria-hidden="true" data-pc-section="dropdownicon"><path d="M10.7838 1.51351H9.83783V0.567568C9.83783 0.417039 9.77804 0.272676 9.6716 0.166237C9.56516 0.0597971 9.42079 0 9.27027 0C9.11974 0 8.97538 0.0597971 8.86894 0.166237C8.7625 0.272676 8.7027 0.417039 8.7027 0.567568V1.51351H5.29729V0.567568C5.29729 0.417039 5.2375 0.272676 5.13106 0.166237C5.02462 0.0597971 4.88025 0 4.72973 0C4.5792 0 4.43484 0.0597971 4.3284 0.166237C4.22196 0.272676 4.16216 0.417039 4.16216 0.567568V1.51351H3.21621C2.66428 1.51351 2.13494 1.73277 1.74467 2.12305C1.35439 2.51333 1.13513 3.04266 1.13513 3.59459V11.9189C1.13513 12.4709 1.35439 13.0002 1.74467 13.3905C2.13494 13.7807 2.66428 14 3.21621 14H10.7838C11.3357 14 11.865 13.7807 12.2553 13.3905C12.6456 13.0002 12.8649 12.4709 12.8649 11.9189V3.59459C12.8649 3.04266 12.6456 2.51333 12.2553 2.12305C11.865 1.73277 11.3357 1.51351 10.7838 1.51351ZM3.21621 2.64865H4.16216V3.59459C4.16216 3.74512 4.22196 3.88949 4.3284 3.99593C4.43484 4.10237 4.5792 4.16216 4.72973 4.16216C4.88025 4.16216 5.02462 4.10237 5.13106 3.99593C5.2375 3.88949 5.29729 3.74512 5.29729 3.59459V2.64865H8.7027V3.59459C8.7027 3.74512 8.7625 3.88949 8.86894 3.99593C8.97538 4.10237 9.11974 4.16216 9.27027 4.16216C9.42079 4.16216 9.56516 4.10237 9.6716 3.99593C9.77804 3.88949 9.83783 3.74512 9.83783 3.59459V2.64865H10.7838C11.0347 2.64865 11.2753 2.74831 11.4527 2.92571C11.6301 3.10311 11.7297 3.34371 11.7297 3.59459V5.67568H2.27027V3.59459C2.27027 3.34371 2.36993 3.10311 2.54733 2.92571C2.72473 2.74831 2.96533 2.64865 3.21621 2.64865ZM10.7838 12.8649H3.21621C2.96533 12.8649 2.72473 12.7652 2.54733 12.5878C2.36993 12.4104 2.27027 12.1698 2.27027 11.9189V6.81081H11.7297V11.9189C11.7297 12.1698 11.6301 12.4104 11.4527 12.5878C11.2753 12.7652 11.0347 12.8649 10.7838 12.8649Z" fill="currentColor"></path></svg>
        </Button>
      </div>
    </div>
    <div class="p-range-slider-result">
      <div>
        <input
          ref="minDateInputRef"
          class="input-date"
          placeholder="Jahr oder Datum"
          v-model.lazy="formattedSelectedMinDate"
        />
      </div>
      <div>
        <input
          ref="maxDateInputRef"
          class="input-date"
          placeholder="Jahr oder Datum"
          v-model.lazy="formattedSelectedMaxDate"
        />
      </div>
    </div>
  </div>

  <OverlayPanel ref="minDateCalendarPanel" append-to="body" :close-on-escape="closeOnEscape">
    <Calendar
      v-bind="{ minDate, maxDate: minDateCalendarMax, view: primaryAspect }"
      v-model="selectedMinDate"
      inline
    />
  </OverlayPanel>
  <OverlayPanel ref="maxDateCalendarPanel" append-to="body" :close-on-escape="closeOnEscape">
    <Calendar
      v-bind="{ minDate: maxDateCalendarMin, maxDate, view: primaryAspect }"
      v-model="selectedMaxDate"
      inline
    />
  </OverlayPanel>
</template>

<style lang="scss">
@use '@/styles/util' as *;

.p-range-slider-layout {
  display: flex;
  gap: 1rem;
  align-items: center;

  .p-range-slider-slider {
    flex-grow: 1;
  }
}

.p-range-slider-result {
  margin-top: 0.125rem;
  display: flex;
  justify-content: space-between;
  font-weight: 500;
}

.input-date {
  @include form-reset;
  font-family: $fontFamily;
  font-weight: normal;
  text-decoration: none;
  padding: 0 0.05em;
  background-color: transparent;
  border-bottom: 1px solid $shade300;

  &:focus {
    outline: none;
    border-bottom-color: $inputFocusBorderColor;
  }
}
</style>
